import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEqual } from 'lodash';

import {
    ActionDelete,
    FlexColumn,
    Loading,
    Popover,
    SvgIcon,
    TabsContainer,
    TabsMenu,
    TabsMenuItem,
    TabsPanel,
} from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { DataTableQueryParams } from '@portal/common/types';

import api from '../../api';
import FavoriteResourceList from '../../components/FavoriteResourceList';
import _ from '../../locale';
import { GROUP_CONTENT_ID, MY_FAVORITES_ID } from '../../router/paths';
import { getOrganizationId } from '../../store/organization/selectors';
import { getPublicLists } from '../../store/resource-lists/selectors';
import { getSessionAccountId } from '../../store/session/selectors';
import { removeDashboardTab as removeDashboardTabAction } from '../../store/user-settings/actions';
import { getDashboardTabs } from '../../store/user-settings/selectors';
import DropdownWithLists from './DropdownWithLists';

const DropdownIcon = (props) => (
    <SvgIcon
        alt="Dropdown Menu"
        src="/icons/ic-arrow-down.svg"
        style={{ marginLeft: 8, transform: 'scale(1.3)', width: 10 }}
        {...props}
    />
);

const StyledTabsMenuItem = styled(TabsMenuItem)({});

const StyledDeleteIcon = styled(ActionDelete)(({ theme }) => ({
    marginLeft: 8,
}));

const tabsMenuStyle = {
    overflow: 'auto',
    height: 'auto',
};

const dataTableStyle = { tableLayout: 'fixed' };

const DEFAULT_QUERY_PARAMS = {
    page: '1',
    per_page: '5',
    order_by: 'created',
    order_direction: 'DESC',
    is_admin_created: 0,
};

const defaultState = {
    selectedTabIndex: 0,
    queryParams: { ...DEFAULT_QUERY_PARAMS },
};

type State = {
    selectedTabIndex: number;
    queryParams: DataTableQueryParams;
};

const TabbedView: FC<{}> = () => {
    const dispatch = useDispatch();
    const memberId = useSelector(getSessionAccountId);
    const organizationId = useSelector(getOrganizationId);
    const publicLists = useSelector(getPublicLists);
    const dashboardTabs = useSelector(getDashboardTabs);
    const isWelcomeMessageDismissed = useSelector(
        (state) => state.userSettings.dismissedWelcomeMessageHash
    );
    const [state, setState] = useState<State>(defaultState);

    const removeDashboardTab = useCallback((payload) => {
        dispatch(removeDashboardTabAction(payload));
    }, []);

    const getApiParams = useCallback(
        (params?: any) => ({
            organizationId,
            memberId,
            ...DEFAULT_QUERY_PARAMS,
            ...params,
        }),
        [memberId, organizationId]
    );

    const loadResources = useCallback(
        (listId) => {
            let loadItems: any = null;
            if (listId === MY_FAVORITES_ID) {
                loadItems = (params) =>
                    api.organizationMemberFavoriteResource.getFavoriteResources(
                        getApiParams(params)
                    );
            } else if (listId === GROUP_CONTENT_ID) {
                loadItems = (params) =>
                    api.organizationGroupContent.getLinksAndFiles(getApiParams(params));
            } else {
                loadItems = (params) =>
                    api.organizationResourceListResource.getResourceListResources(
                        getApiParams({
                            ...params,
                            listId,
                        })
                    );
            }

            return loadItems;
        },
        [getApiParams]
    );

    const getQueryParams = useCallback(
        () => ({
            ...state.queryParams,
            per_page: isWelcomeMessageDismissed ? '10' : '5',
        }),
        [state.queryParams, isWelcomeMessageDismissed]
    );

    const setQueryParams = useCallback(
        (queryParams) => {
            if (!isEqual(queryParams, state.queryParams)) {
                setState({ ...state, queryParams });
            }
        },
        [state]
    );

    const handleTabChange = useCallback((selectedTabIndex) => {
        setState({ selectedTabIndex, queryParams: { ...DEFAULT_QUERY_PARAMS } });
    }, []);

    if (publicLists == null) {
        return <Loading />;
    }

    return (
        <TabsContainer selectedIndex={state.selectedTabIndex} onSelect={handleTabChange}>
            <TabsMenu style={tabsMenuStyle}>
                {dashboardTabs.map((tabList, tabIndex) => {
                    const isActiveTab = tabIndex === state.selectedTabIndex;

                    return (
                        <TabsMenuItem key={tabIndex} tabIndex="true">
                            <Popover
                                renderContent={({ toggleVisibility }) => (
                                    <DropdownWithLists
                                        tabListId={tabList.id}
                                        tabIndex={tabIndex}
                                        toggleVisibility={toggleVisibility}
                                    />
                                )}
                            >
                                {({ ref, toggleVisibility }) => (
                                    <div
                                        ref={ref}
                                        css={{
                                            '#delete-icon': { visibility: 'hidden' },
                                            ':hover #delete-icon': {
                                                visibility: 'visible',
                                            },
                                        }}
                                        {...(isActiveTab && {
                                            onClick: toggleVisibility,
                                        })}
                                    >
                                        {tabList.name}
                                        {isActiveTab ? (
                                            <DropdownIcon />
                                        ) : (
                                            <StyledDeleteIcon
                                                data-testid="delete-icon"
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    removeDashboardTab(tabIndex);
                                                    if (tabIndex < state.selectedTabIndex) {
                                                        setState({
                                                            ...state,
                                                            selectedTabIndex:
                                                                state.selectedTabIndex - 1,
                                                        });
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </Popover>
                        </TabsMenuItem>
                    );
                })}

                <StyledTabsMenuItem>
                    <Popover
                        renderContent={({ toggleVisibility }) => (
                            <DropdownWithLists toggleVisibility={toggleVisibility} />
                        )}
                    >
                        {({ ref, toggleVisibility }) => (
                            <div ref={ref}>
                                <div
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        toggleVisibility();
                                    }}
                                >
                                    {_.get('lists_add_new_tab')}
                                </div>
                            </div>
                        )}
                    </Popover>
                </StyledTabsMenuItem>
            </TabsMenu>

            <FlexColumn grow={1}>
                {dashboardTabs.map((tabList) => (
                    <TabsPanel key={tabList.tabId}>
                        <FavoriteResourceList
                            listId={tabList.id}
                            key={tabList.tabId}
                            disableSorting
                            queryParams={getQueryParams()}
                            setQueryParams={setQueryParams}
                            dataTableStyle={dataTableStyle}
                            loadItems={loadResources(tabList.id)}
                        />
                    </TabsPanel>
                ))}
            </FlexColumn>
        </TabsContainer>
    );
};

export default TabbedView;
